/**
 * EU AI Act risk-tier classifier — pure logic, shared between the page UI and
 * unit tests. Keeping this out of the React component makes the tier decision
 * testable in isolation (node vitest, no DOM required) and prevents drift when
 * we later add an EU-language port of the copy.
 *
 * Tier assignment follows the EU AI Act structure:
 *   - Prohibited practices                 (Article 5)
 *   - High-risk systems                    (Article 6 + Annex III)
 *   - Limited-risk / transparency          (Article 50)
 *   - GPAI models with systemic risk       (Article 51 — >10^25 FLOPs threshold)
 *   - Minimal-risk (residual)              (no specific obligations)
 *
 * The classifier is intentionally a simple ordered rule engine: the FIRST
 * matching rule wins. Rules are ordered from most-restrictive downward so a
 * high-risk employment use-case is not misclassified as merely limited-risk
 * because the same system also happens to be a chatbot.
 */

export const RISK_TIER_IDS = [
  'prohibited',
  'high-risk',
  'gpai-systemic',
  'limited-risk',
  'minimal-risk',
] as const;

export type RiskTierId = (typeof RISK_TIER_IDS)[number];

export const QUESTION_IDS = [
  'biometric',
  'critical-infrastructure',
  'employment',
  'law-enforcement',
  'human-interaction',
  'generative-content',
  'gpai-large',
] as const;

export type QuestionId = (typeof QUESTION_IDS)[number];

export interface Question {
  id: QuestionId;
  prompt: string;
  helper: string;
}

/**
 * Fixed question order. The page renders these in-order and the URL hash
 * encoder relies on a stable index-based sequence.
 */
export const QUESTIONS: readonly Question[] = [
  {
    id: 'biometric',
    prompt: 'Does your AI system perform biometric identification or categorisation of people?',
    helper:
      'Includes real-time or post remote biometric identification, biometric categorisation by sensitive attributes, and emotion recognition in workplaces or education.',
  },
  {
    id: 'critical-infrastructure',
    prompt:
      'Is the system a safety component of critical infrastructure (road traffic, water, gas, heating, electricity)?',
    helper: 'Annex III §2 — management and operation of critical digital infrastructure.',
  },
  {
    id: 'employment',
    prompt:
      'Is it used for employment, worker management, or access to essential public/private services (credit scoring, insurance risk, benefits)?',
    helper: 'Annex III §4 and §5 — high-risk regardless of provider size.',
  },
  {
    id: 'law-enforcement',
    prompt:
      'Is it used by law enforcement, for migration/border/asylum management, or in administration of justice and democratic processes?',
    helper: 'Annex III §6, §7, §8 — high-risk with extra Article 26 obligations for deployers.',
  },
  {
    id: 'human-interaction',
    prompt:
      'Does the system interact directly with natural persons (chatbot, virtual assistant, voice agent)?',
    helper:
      'Article 50 — users must be informed they are interacting with an AI system unless obvious from context.',
  },
  {
    id: 'generative-content',
    prompt:
      'Does the system generate synthetic content (text, image, audio, or video), including deep fakes?',
    helper:
      'Article 50 — outputs must be marked as artificially generated in a machine-readable format; deep fakes require conspicuous disclosure.',
  },
  {
    id: 'gpai-large',
    prompt:
      'Is this a general-purpose AI model whose training used more than 10^25 floating-point operations of compute?',
    helper:
      'Article 51 — models above this threshold are presumed to have systemic risk and carry Article 55 obligations (evaluations, adversarial testing, incident reporting, cybersecurity).',
  },
] as const;

export interface RiskTier {
  id: RiskTierId;
  label: string;
  tagline: string;
  summary: string;
  obligations: readonly string[];
  reference: string;
}

/**
 * Tier definitions — the source of truth for the copy shown on the result
 * page. Keep obligations to 3–5 bullets each; deeper detail belongs in the
 * downloadable assessment report, not the free tool.
 */
export const RISK_TIERS: Record<RiskTierId, RiskTier> = {
  prohibited: {
    id: 'prohibited',
    label: 'Prohibited practice',
    tagline: 'Not permitted to be placed on the EU market.',
    summary:
      'Uses covered by Article 5 (including untargeted facial-image scraping, social scoring by public authorities, real-time remote biometric identification in public spaces except narrow law-enforcement carve-outs, and manipulative or exploitative systems) cannot be sold, deployed, or used in the EU.',
    obligations: [
      'Cease placing on the EU market and withdraw any existing deployments.',
      'Assess whether a narrow Article 5 exception applies (limited law-enforcement use requires prior judicial authorisation and Member State law).',
      'Document the classification decision — regulators may request the reasoning.',
      'Consider redesigning the system so it falls into a lower risk tier before returning to market.',
    ],
    reference: 'EU AI Act, Article 5 (prohibited AI practices) — in force since 2 February 2025.',
  },
  'high-risk': {
    id: 'high-risk',
    label: 'High-risk system',
    tagline: 'Subject to the full Chapter III conformity regime.',
    summary:
      'High-risk systems (Article 6 + Annex III use cases such as biometric identification, critical infrastructure, education, employment, essential services, law enforcement, migration, and administration of justice) must complete conformity assessment before market entry and comply with ongoing obligations for providers and deployers.',
    obligations: [
      'Establish a documented risk-management system covering the full lifecycle (Art. 9).',
      'Meet data-governance, technical-documentation, and record-keeping requirements (Art. 10–12).',
      'Ensure human oversight, accuracy, robustness, and cybersecurity by design (Art. 13–15).',
      'Register the system in the EU database, run conformity assessment, and affix CE marking (Art. 43, 48, 71).',
      'Deployers must complete a Fundamental Rights Impact Assessment where required (Art. 27) and monitor operation.',
    ],
    reference:
      'EU AI Act, Article 6 and Annex III — high-risk obligations phased in through 2 August 2026 and 2 August 2027.',
  },
  'gpai-systemic': {
    id: 'gpai-systemic',
    label: 'GPAI with systemic risk',
    tagline: 'Frontier general-purpose model obligations.',
    summary:
      'General-purpose AI models trained with more than 10^25 FLOPs are presumed to have systemic risk under Article 51. In addition to the baseline GPAI obligations (Article 53), providers must run model evaluations, adversarial testing, incident reporting, and adequate cybersecurity protections.',
    obligations: [
      'Notify the AI Office within 2 weeks of meeting or being designated as meeting the systemic-risk threshold (Art. 52).',
      'Perform model evaluations including standardised protocols and adversarial testing (Art. 55(1)(a)).',
      'Assess and mitigate possible systemic risks at Union level, including their sources (Art. 55(1)(b)).',
      'Report serious incidents and possible corrective measures to the AI Office and national authorities (Art. 55(1)(c)).',
      'Ensure an adequate level of cybersecurity protection for the model and its physical infrastructure (Art. 55(1)(d)).',
    ],
    reference: 'EU AI Act, Article 51–55 — GPAI obligations applicable from 2 August 2025.',
  },
  'limited-risk': {
    id: 'limited-risk',
    label: 'Limited-risk system',
    tagline: 'Transparency obligations under Article 50.',
    summary:
      'Systems that interact with people or generate synthetic content are not high-risk by default, but users have a right to know they are dealing with (or looking at output from) an AI. The obligations are targeted transparency measures rather than the full high-risk regime.',
    obligations: [
      'Inform users, in a clear and distinguishable way, that they are interacting with an AI system unless it is obvious from context (Art. 50(1)).',
      'Mark AI-generated or manipulated content in a machine-readable format detectable as artificial (Art. 50(2)).',
      'Disclose deep fakes conspicuously to natural persons exposed to the content (Art. 50(4)).',
      'Where applicable, disclose the use of emotion recognition or biometric categorisation to affected persons (Art. 50(3)).',
    ],
    reference: 'EU AI Act, Article 50 — transparency obligations applicable from 2 August 2026.',
  },
  'minimal-risk': {
    id: 'minimal-risk',
    label: 'Minimal-risk system',
    tagline: 'No specific EU AI Act obligations.',
    summary:
      'The overwhelming majority of AI systems used in the EU today fall into this residual bucket — spam filters, inventory optimisation, product recommenders that do not affect access to essential services, and similar. No EU AI Act obligations apply, though voluntary codes of conduct (Art. 95) and general product-safety, data-protection, and sector rules still do.',
    obligations: [
      'No mandatory EU AI Act obligations, but re-assess whenever the use case changes.',
      'Consider adopting the AI Act code of conduct (Art. 95) as a voluntary trust signal.',
      'Continue to comply with GDPR, sector-specific regulation, and product-safety law.',
      'Monitor whether upstream GPAI models you rely on are classified as systemic-risk — that may raise your obligations.',
    ],
    reference: 'EU AI Act — no specific obligations; voluntary codes of conduct under Article 95.',
  },
};

export type Answers = Partial<Record<QuestionId, boolean>>;

/**
 * Classify a set of answers into a risk tier. The rule order is load-bearing:
 * a high-risk use-case that also happens to include a chatbot layer must
 * classify as high-risk, not as limited-risk.
 *
 * Unanswered questions are treated as "No" for classification purposes so a
 * partial result URL still resolves to a tier (the page also guards against
 * navigating to the result before all questions are answered).
 */
export function classify(answers: Answers): RiskTierId {
  if (answers.biometric) return 'prohibited';
  if (answers['critical-infrastructure']) return 'high-risk';
  if (answers.employment) return 'high-risk';
  if (answers['law-enforcement']) return 'high-risk';
  if (answers['gpai-large']) return 'gpai-systemic';
  if (answers['human-interaction']) return 'limited-risk';
  if (answers['generative-content']) return 'limited-risk';
  return 'minimal-risk';
}

/**
 * URL-safe encoding of the answer set. We use a fixed-length base-3 string
 * indexed by QUESTIONS order: 'y' = yes, 'n' = no, '-' = unanswered. This
 * keeps result URLs stable, shareable, and inspectable without a server.
 */
export function encodeAnswers(answers: Answers): string {
  return QUESTIONS.map((q) => {
    const value = answers[q.id];
    if (value === true) return 'y';
    if (value === false) return 'n';
    return '-';
  }).join('');
}

export function decodeAnswers(encoded: string): Answers {
  if (typeof encoded !== 'string' || encoded.length !== QUESTIONS.length) {
    return {};
  }
  const result: Answers = {};
  for (let i = 0; i < QUESTIONS.length; i++) {
    const char = encoded[i];
    if (char === 'y') result[QUESTIONS[i].id] = true;
    else if (char === 'n') result[QUESTIONS[i].id] = false;
  }
  return result;
}

/**
 * True iff every question has been answered. The result page requires this
 * so we don't silently classify a half-filled questionnaire as minimal-risk.
 */
export function isComplete(answers: Answers): boolean {
  return QUESTIONS.every((q) => typeof answers[q.id] === 'boolean');
}

/**
 * The English-word table used by `numberWord()`. Exported so tests (and any
 * future copy-drift guard) can iterate the full set without duplicating it —
 * a duplicated list silently rots when the table grows (e.g. adding `ten`
 * without updating the test guard means a `"ten yes/no questions"` typo
 * slips past). See #12258.
 */
export const NUMBER_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
] as const;

/**
 * Render a small integer as its English word — used so hero + meta copy
 * templates off `QUESTIONS.length` and self-heals when the questionnaire is
 * extended or trimmed. Falls back to the numeric string for values outside
 * the small-range table (we only ever have a single-digit question count).
 */
export function numberWord(n: number): string {
  if (Number.isInteger(n) && n >= 0 && n < NUMBER_WORDS.length) return NUMBER_WORDS[n];
  return String(n);
}

function capitalize(word: string): string {
  if (word.length === 0) return word;
  return word[0].toUpperCase() + word.slice(1);
}

/**
 * The hero + meta-description intro line, templated off `QUESTIONS.length` so
 * the visible "N yes/no questions" claim and the progress indicator ("Question
 * X of N") can never drift out of sync. A visitor promised "eight questions"
 * who then watches a 1-of-7 counter tick through loses trust in the tier
 * assignment — that concrete failure mode is what this indirection prevents.
 */
export const HERO_INTRO_COPY = `${capitalize(numberWord(QUESTIONS.length))} yes/no questions. No signup. In under two minutes you'll know whether your AI use case is prohibited, high-risk, subject to transparency obligations, a systemic-risk GPAI model, or minimal-risk under the EU AI Act.`;

export const META_DESCRIPTION_COPY = `Free, no-signup EU AI Act classifier. Answer ${numberWord(QUESTIONS.length)} yes/no questions and get your system's risk tier under Articles 5, 6, 50, and 51 — plus the obligations that apply.`;

export const OG_DESCRIPTION_COPY = `Answer ${numberWord(QUESTIONS.length)} yes/no questions. Get your EU AI Act risk tier and the obligations that apply.`;
